import { useEffect, useState } from "react";
import classNames from "../../../utils/classNames";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

export interface CardProps {
  title?: string;
  children: React.ReactNode;
  labelId?: string;
  containerClassName?: string;
  bodyClassName?: string;
  titleClassName?: string;
  subtitle?: string;
  headerRight?: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export default function Card({ title, labelId, children, containerClassName, bodyClassName = "", titleClassName = "", subtitle, headerRight, collapsible = false, defaultOpen = true, onToggle }: CardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  const toggle = () => {
    if (collapsible) {
      setIsOpen((prev) => {
        const newValue = !prev;
        onToggle?.(newValue);
        return newValue;
      });
    }
  };

  return (
    <div className={containerClassName} aria-labelledby={`${labelId ?? "card"}-title`}>
      <div className="bg-white shadow rounded-lg">
        {title &&
          <div className={classNames(collapsible ? "cursor-pointer select-none" : "", titleClassName, "rounded-lg px-4 py-5 sm:px-6")} onClick={toggle}>
            <h2
              id={`$${labelId ?? "card"}-title`}
              className="flex justify-between text-lg font-medium leading-6 text-gray-900"
            >
              <span>{title}</span>
              <span className="flex items-center gap-2">
                {headerRight}
                <ChevronDownIcon
                  className={classNames(
                    collapsible ? "h-6 w-6" : "hidden",
                    isOpen ? "" : "rotate-180",
                    "text-gray-400 hover:text-gray-500 transition-transform duration-200 ease-in-out"
                  )}
                />
              </span>
            </h2>
            {subtitle && (
              <small className="text-sm font-medium text-gray-500 mt-2">{subtitle}</small>
            )}
          </div>
        }
        <div className={classNames(
          "transition-all duration-300 ease-in-out",
          collapsible ? (isOpen ? `max-h-full opacity-100 ${bodyClassName}` : "max-h-0 opacity-0 pointer-events-none") : `${bodyClassName}`,
          "border-t border-gray-200",
        )}>
          {children}
        </div>
      </div>

    </div>
  );
}
